// Guards
import authRoutes from "./auth";
import sharedRoutes from "./shared";
import superuserRoutes from "./superuser";
import userRoutes from "./user";
import AuthGuard from "components/guards/AuthGuard";
import ConsentGuard from "components/guards/ConsentGuard";
import GuestGuard from "components/guards/GuestGuard";
import RegularUserGuard from "components/guards/RegularUserGuard";
import SuperUserGuard from "components/guards/SuperUserGuard";
import { CapabilitiesProvider } from "contexts/CapabilitiesContext";
// Layouts
import AuthLayout from "layouts/Auth";
import DashboardLayout from "layouts/Dashboard";
// Common
import Page404 from "pages/auth/Page404";
import { lazy } from "react";

const PolicyPage = lazy(() => import("pages/legal/PolicyPage"));

const SubscriptionPendingActivationPage = lazy(() =>
  import("pages/subscriptions/SubscriptionPendingActivationPage")
);
const SubscriptionPendingCancellationPage = lazy(() =>
  import("pages/subscriptions/SubscriptionPendingCancellationPage")
);

const NativeLogin = lazy(() => import("pages/auth/NativeLogin"));
const OAuthAuthorizePage = lazy(() => import("pages/oauth/OAuthAuthorizePage"));

const routes = [
  {
    path: "/",
    element: (
      <RegularUserGuard>
        <ConsentGuard>
          <DashboardLayout />
        </ConsentGuard>
      </RegularUserGuard>
    ),
    children: userRoutes,
  },
  {
    path: "/",
    element: (
      <RegularUserGuard>
        <AuthLayout />
      </RegularUserGuard>
    ),
    children: [
      {
        path: "plans/pending-activation",
        element: <SubscriptionPendingActivationPage />,
      },
      {
        path: "plans/pending-cancellation",
        element: <SubscriptionPendingCancellationPage />,
      },
    ],
  },
  {
    path: "/",
    element: (
      <AuthGuard>
        <ConsentGuard>
          <DashboardLayout />
        </ConsentGuard>
      </AuthGuard>
    ),
    children: sharedRoutes,
  },
  {
    // Public, deliberately unguarded: the signup form is pre-auth, so a
    // prospective user has to be able to read the privacy policy before
    // submitting personal data. Also linked from marketing email and the
    // marketing site.
    path: "legal",
    element: <AuthLayout />,
    children: [
      {
        path: "privacy",
        element: <PolicyPage documentType="privacy" />,
      },
      {
        path: "terms",
        element: <PolicyPage documentType="terms" />,
      },
      // Addressable historical versions, so a consent record can link to the
      // exact text that was accepted rather than to whatever is current now.
      {
        path: "privacy/v/:version",
        element: <PolicyPage documentType="privacy" />,
      },
      {
        path: "terms/v/:version",
        element: <PolicyPage documentType="terms" />,
      },
    ],
  },
  {
    path: "admin",
    element: (
      <SuperUserGuard>
        <DashboardLayout />
      </SuperUserGuard>
    ),
    children: superuserRoutes,
  },
  {
    path: "auth",
    element: (
      <GuestGuard>
        <AuthLayout />
      </GuestGuard>
    ),
    children: authRoutes,
  },
  {
    // Native-app sign-in handoff (Podium Broadcaster): self-guards via a
    // sign-in redirect with returnTo, so signed-in users are not bounced
    // away as GuestGuard would.
    path: "native-login",
    element: <AuthLayout />,
    children: [
      {
        path: "",
        element: <NativeLogin />,
      },
    ],
  },
  {
    // OAuth 2.0 consent: the API's GET /oauth/authorize/ redirects here with
    // the client's query string. Self-guards via a sign-in redirect with
    // returnTo (like native-login), so a signed-in user is not bounced away.
    path: "oauth",
    element: <AuthLayout />,
    children: [
      {
        path: "authorize",
        // The consent page is gated on the rest_api capability, which the
        // dashboard layout provides and this one does not.
        element: (
          <CapabilitiesProvider>
            <OAuthAuthorizePage />
          </CapabilitiesProvider>
        ),
      },
    ],
  },
  {
    path: "*",
    element: <AuthLayout />,
    children: [
      {
        path: "*",
        element: <Page404 />,
      },
    ],
  },
];

export default routes;
